import { Fragment } from "react";
import { cn } from "@/lib/cn";
import { MOBILE_PRO_CHROME_SUB_STRIP_CLASS } from "./mobile-pro-chrome";

export interface MobileSubTab<T extends string = string> {
  id: T;
  label: string;
  badge?: string;
}

export interface MobileSubTabStripProps<T extends string = string> {
  tabs: MobileSubTab<T>[];
  active: T;
  onChange: (id: T) => void;
  ariaLabel?: string;
  className?: string;
}

function MobileSubTabStrip<T extends string>({
  tabs,
  active,
  onChange,
  ariaLabel = "Settings sections",
  className,
}: MobileSubTabStripProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        MOBILE_PRO_CHROME_SUB_STRIP_CLASS,
        "overflow-x-auto overscroll-x-contain scroll-px-4",
        className,
      )}
    >
      {tabs.map((tab, index) => {
        const isActive = active === tab.id;
        return (
          <Fragment key={tab.id}>
            {index > 0 ? (
              <div
                className="m-tab-separator h-[18px] w-px shrink-0 self-center bg-pro-border-subtle"
                aria-hidden
              />
            ) : null}
            <button
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(tab.id)}
              className={cn(
                "m-tab inline-flex h-11 shrink-0 items-center whitespace-nowrap border-b-2 px-2.5 text-[12px] font-semibold transition-colors",
                isActive
                  ? "active border-lime text-lime"
                  : "border-transparent text-pro-text-muted hover:text-pro-text",
              )}
            >
              {tab.label}
              {tab.badge ? (
                <span
                  className={cn(
                    "tab-soon ml-1 rounded-full bg-pro-elevated px-[5px] py-px text-[8px] font-bold uppercase tracking-[0.06em] text-pro-text-muted",
                    isActive && "text-lime",
                  )}
                >
                  {tab.badge}
                </span>
              ) : null}
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}

export { MobileSubTabStrip };
